import { Component, input, output, computed, inject, effect, untracked, OnDestroy, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { MultipleBarComponent } from '../../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { PieChartComponent } from '../../../../../shared/components/charts/pie-chart/pie-chart.component';
import { AiHeadlinesComponent } from '../../../../../shared/components/ai-headlines/ai-headlines.component';
import { CommonService } from '../../../../../core/services/common.service';
import { MobileBackNavigationService } from '../../../../../core/services/mobile-back-navigation.service';
@Component({
  selector: 'app-monthly-report-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, MultipleBarComponent, LineChartComponent, PieChartComponent, AiHeadlinesComponent],
  templateUrl: './monthly-report-mobile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe]
})
export class MonthlyReportMobileComponent implements OnDestroy {
  private commonService = inject(CommonService);
  private _mobileBackNav = inject(MobileBackNavigationService);
  private _elementRef = inject(ElementRef);
  showNav = this.commonService.showMobileNav;
  showFloatingBar = this.commonService.showMobileFloatingBar;

  private readonly MOBILE_DETAIL_BACK_ID = 'monthlyReport1MobileDetail';
  private previousDetailedView: string | null = null;

  constructor() { 
    effect(() => {
      const current = this.mobileDetailedView();
      untracked(() => {
        const prev = this.previousDetailedView;
        if (current && !prev) {
          if (!this._mobileBackNav.isActive(this.MOBILE_DETAIL_BACK_ID)) {
            this._mobileBackNav.push(this.MOBILE_DETAIL_BACK_ID, () => {
              this.detailClosed.emit();
            });
          }
        } else if (!current && prev) {
          if (this._mobileBackNav.isActive(this.MOBILE_DETAIL_BACK_ID)) {
            this._mobileBackNav.pop(this.MOBILE_DETAIL_BACK_ID);
          }
        }
        if (current !== prev) {
          this.scrollViewToTop();
        }
        this.previousDetailedView = current;
      });
    });
  }

  // Ensures every detail/main view opens at the top instead of inheriting
  // the previous view's scroll position.
  // The real scroll container is a nested `overflow-y-auto` div in the
  // private layout (not `window`). We walk up the DOM from this component's
  // host element and reset every scrollable ancestor. Double rAF waits for
  // Angular to render the new view before we scroll.
  private scrollViewToTop(): void {
    const reset = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.scrollingElement?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.body?.scrollTo({ top: 0, left: 0, behavior: 'auto' });

        let el: HTMLElement | null = this._elementRef?.nativeElement ?? null;
        while (el && el !== document.body && el !== document.documentElement) {
          const style = getComputedStyle(el);
          const oy = style.overflowY;
          const ox = style.overflowX;
          if (oy === 'auto' || oy === 'scroll' || ox === 'auto' || ox === 'scroll') {
            if (el.scrollTop !== 0) el.scrollTop = 0;
            if (el.scrollLeft !== 0) el.scrollLeft = 0;
          }
          el = el.parentElement;
        }
      } catch {
        window.scrollTo(0, 0);
      }
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(reset));
    } else {
      setTimeout(reset, 0);
    }
  }

  ngOnDestroy(): void {
    this._mobileBackNav.cleanup(this.MOBILE_DETAIL_BACK_ID);
  }

  // Inputs
  isLoading = input<boolean>(false);
  startDate = input<string | null>(null);
  endDate = input<string | null>(null);
  activeBusiness = input<any>(null);
  // previousActiveBusiness = input<any>(null);
  maxActiveBusinessValue = input<number>(0);
  podiumConfig = input<any[]>([]);
  categoryPodiumConfig = input<any[]>([]);
  enrolledCurrent = input<number>(0);
  enrolledGrowth = input<number>(0);
  contractsCurrent = input<number>(0);
  contractsGrowth = input<number>(0);
  businessContractTypes = input<any[]>([]);
  
  fullEnrolledBusinessList = input<any[]>([]);
  fullRegionalContractsList = input<any[]>([]);
  fullCategoryList = input<any[]>([]);
  overallContractsList = input<any[]>([]);
  overallContractsTotal = input<number>(0);
  remainingTopBusinesses = input<any[]>([]);
  
  mobileDetailedView = input<string | null>(null);
  mobileChartType = input<'bar' | 'line'>('bar');
  enrolledSearchQuery = input<string>('');
  contractsSearchQuery = input<string>('');
  // Driven by the parent date-range picker so chart x-axis labels respect
  // the selected granularity ('hour' | 'day' | 'week' | 'month' | 'quarter' | 'year').
  unit = input<string>('day');

  // Outputs
  aiRequested = output<void>();
  filterRequested = output<void>();
  detailOpened = output<string>();
  detailClosed = output<void>();
  chartTypeChanged = output<'bar' | 'line'>();
  enrolledSearchQueryChanged = output<string>();
  contractsSearchQueryChanged = output<string>();

  // Helper properties/methods for the template
  colors = {
    data1: '#ac46ffd5' as `#${string}`,
    data2: '' as const,
    data3: '' as const,
    data4: '' as const,
  };

  // Minimum horizontal pixels reserved per bar in the scrollable mobile chart.
  // The chart container is at least this many pixels wide per data point so bars
  // don't get crammed together when there are many data points. The outer
  // container becomes horizontally scrollable when the calculated width exceeds
  // the available viewport width.
  private readonly MOBILE_CHART_PIXELS_PER_BAR = 60;
  private readonly MOBILE_CHART_MIN_WIDTH = 320;

  activeBusinessChartScrollWidth = computed(() => {
    const length = this.activeBusiness()?.chartData?.length ?? 0;
    const calculated = length * this.MOBILE_CHART_PIXELS_PER_BAR;
    return Math.max(calculated, this.MOBILE_CHART_MIN_WIDTH);
  });

  openFilterSheet() {
    this.filterRequested.emit();
  }

  openAiPanel() {
    this.aiRequested.emit();
  }

  openMobileDetail(type: string) {
    if (this.mobileDetailedView() === type) {
      if (this._mobileBackNav.isActive(this.MOBILE_DETAIL_BACK_ID)) {
        this._mobileBackNav.pop(this.MOBILE_DETAIL_BACK_ID);
      } else {
        this.detailClosed.emit();
      }
    } else {
      this.detailOpened.emit(type);
      // `scrollViewToTop()` also runs from the constructor effect when
      // `mobileDetailedView()` changes, but we trigger it here as well so
      // the page resets immediately even if the parent updates the signal
      // a moment later.
      this.scrollViewToTop();
    }
  }

  closeMobileDetail() {
    this.detailClosed.emit();
  }

  setMobileChartType(type: 'bar' | 'line') {
    this.chartTypeChanged.emit(type);
  }

  updateEnrolledSearch(query: string) {
    this.enrolledSearchQueryChanged.emit(query);
  }

  updateContractsSearch(query: string) {
    this.contractsSearchQueryChanged.emit(query);
  }
}
